import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useMatches, useSearch } from '@tanstack/react-router';
import {
  type PropsWithChildren,
  Suspense,
  createContext,
  useContext,
  useMemo,
} from 'react';
import {
  getStepNameFromPathName,
  type SignupSubRouteRouteContext,
} from 'router/utils';

import { PageSkeleton } from 'components/templates/PageSkeleton';
import { useCurrentStepName } from 'hooks/useCurrentStepName';
import { useLocalization } from 'providers/LocalizationProvider';
import type { Country, Currency, Language } from 'services/i18n';
import type { GoogleMapsLink } from 'services/partnersSelfSignup/googlePlaces/types';
import type { Offer } from 'services/partnersSelfSignup/offers';
import {
  type GetLeadResponse,
  getLeadQueryKey,
  getLeadQueryOptions,
  resolveLeadQueryVariables,
} from 'services/partnersSelfSignup/signup';
import type { Base64File } from 'typings/file';
import type { RequireEither } from 'typings/utils';

const LEAD_STATUS = [
  'ongoing',
  'to_be_qualified',
  'rejected',
  'blocklist',
  'converted',
  'wishlist',
] as const;

const LEAD_STATUS_REASON = [
  'incorrect_data_data_not_found',
  'fraud',
  'ss_tax_id_issue',
  'gym_temporarily_permanently_closed',
  'works_with_a_competitor',
  'former_partner',
  'already_a_partner',
  'no_decision_no_reply',
  'not_willing_to_discuss',
  'is_not_a_gym',
  'appointment_based_model_not_supported_by_gympass_business_model',
  'ss_ongoing_opp',
  'offer_sent',
  'partner_setup',
  'in_negotiation',
  'launched_won',
  'lost',
  'qualification',
  'offer_approved',
  'signed_contract',
] as const;

export type LeadStatusReason = (typeof LEAD_STATUS_REASON)[number] | null;

type LeadStatus = (typeof LEAD_STATUS)[number];

export type Duration = 'annual' | 'semi_annual' | 'quarterly' | 'monthly';
export type UsageRestriction = 0 | 1 | 2 | 3 | 4 | 5 | 6;

type LeadMembershipBase = {
  confirmation: {
    url?: string;
    file?: Base64File;
  };
};

type LegacyMembership = LeadMembershipBase & {
  pricingModel: 'legacy';
  plans: {
    duration: Duration;
    details: {
      usageRestriction: UsageRestriction;
      price: number;
      pricePeriod: Duration;
    }[];
  }[];
};

type MembershipPricing = LeadMembershipBase & {
  pricingModel: 'membership';
  usageRestriction: UsageRestriction;
  userPays: number;
  pricePeriod: Duration;
};

type PackagePricing = LeadMembershipBase & {
  pricingModel: 'package';
  largestPackSize: number;
  userPays: number;
  packageType: 'class' | 'package';
};

export type LeadMembership =
  | LegacyMembership
  | MembershipPricing
  | PackagePricing;

export interface Lead {
  id?: string;
  firstName?: string;
  lastName?: string;
  legalName?: string;
  businessEmail?: string;
  country?: Country;
  currency?: NonNullable<Currency>['iso'];
  businessType?:
    | 'gym'
    | 'studio'
    | 'wellness_center'
    | 'personal_trainer';
  businessName?: string;
  website?: string;
  phone?: string;
  taxId?: string;
  w9?: {
    file?: Base64File;
  };
  googlePlaceId?: string;
  googlePlaceRating?: number;
  googlePlaceTypes?: string[];
  googleTotalUserRating?: number;
  googleMapsLink?: GoogleMapsLink | null;
  formattedAddress?: string;
  additionalAddressInfo?: string;
  createdAt?: string;
  latitude?: number;
  longitude?: number;
  address?: {
    city: string;
    postalCode: string;
    street: string;
  } & RequireEither<{
    state: string;
    stateCode: string;
  }>;
  productName?: string;
  activities?: {
    id: string;
    translationKey: string;
    defaultValue: string;
    category: {
      id: string;
      translationKey: string;
      defaultValue: string;
    };
  }[];
  offer?: Offer;
  activitiesIds?: string[];
  memberships?: LeadMembership;
  digitalCertificate?: {
    file?: Base64File;
  };
  utmTerm?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  ssuCoupon?: string;
  language?: Language;
  status?: LeadStatus;
  statusReason?: LeadStatusReason;
  externalSource?: string;
  experimentVariant?: string;
}

export type LeadUtms = Pick<
  Lead,
  'utmTerm' | 'utmSource' | 'utmMedium' | 'utmContent' | 'utmCampaign'
>;

type LeadProviderProps = Omit<
  GetLeadResponse,
  'step' | 'lastStepSubmitted' | 'stepVersion' | 'stepName'
>;

export type LeadContext = Partial<LeadProviderProps> & {
  updateLead: (data: Partial<LeadProviderProps>) => void;
};

export const LeadContext = createContext<LeadContext>({
  updateLead: () => null,
});

export function LeadProvider({ children }: Readonly<PropsWithChildren>) {
  const queryClient = useQueryClient();
  const search = useSearch({
    strict: false,
  });
  const { language } = useLocalization();
  const currentStepName = useCurrentStepName();
  const [currentMatch] = useMatches({
    select: matches => {
      return matches.filter(
        match => getStepNameFromPathName(match.fullPath) === currentStepName,
      );
    },
  });

  const { queryVariables } =
    (currentMatch?.context as SignupSubRouteRouteContext) ?? {};

  const variables = resolveLeadQueryVariables(queryClient, {
    leadId: queryVariables?.leadId,
  });

  const leadQueryOptions = getLeadQueryOptions(variables);

  const {
    data: { lead, selfSignUpId },
  } = useSuspenseQuery(leadQueryOptions);

  const value = useMemo(() => {
    const updateLead = (data: Partial<GetLeadResponse>) => {
      const {
        utm_term: utmTerm,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_medium: utmMedium,
        utm_source: utmSource,
      } = search;

      const leadId = data.lead?.id ?? variables.leadId;
      const queryKey = getLeadQueryKey({ leadId });

      queryClient.setQueryData<GetLeadResponse>(queryKey, currentLead => {
        const leadRecord = {
          step: data.step ?? currentLead?.step ?? 1,
          stepVersion: data.stepVersion ?? currentLead?.stepVersion ?? 'v0',
          stepName: data.stepName ?? currentLead?.stepName ?? 'empty',
          lastStepSubmitted:
            data.lastStepSubmitted ?? currentLead?.lastStepSubmitted ?? 1,
          selfSignUpId: data.selfSignUpId ?? currentLead?.selfSignUpId ?? '',
          lead: {
            // add utm values if available on search, but preserve existing values
            utmTerm,
            utmCampaign,
            utmContent,
            utmMedium,
            utmSource,
            ...currentLead?.lead,
            ...data.lead,
            language,
          },
        };

        return leadRecord;
      });
    };

    return {
      selfSignUpId,
      lead,
      updateLead,
    };
  }, [selfSignUpId, lead, queryClient, search, variables, language]);

  return (
    <LeadContext.Provider value={value}>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </LeadContext.Provider>
  );
}

export function useLead() {
  const value = useContext(LeadContext);
  return value;
}
